import { handleError } from "@ch/helpers/utils/handleError";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import markMessageAsSeen from "../utils/markMessageAsSeen";
import type { TKemMessagePayload } from "../utils/types";

interface UseKemMarkMessagesSeenProps {
  setCurrentChat: React.Dispatch<React.SetStateAction<TKemMessagePayload[]>>;
  debounceMs?: number;
}

const useKemMarkMessagesSeen = ({
  setCurrentChat,
  debounceMs = 1000,
}: UseKemMarkMessagesSeenProps) => {
  const [pendingSeenMessageIds, setPendingSeenMessageIds] = useState<string[]>(
    [],
  );

  const handleMarkMessageVisible = useCallback((messageId: string) => {
    setPendingSeenMessageIds((prev) => {
      if (prev.includes(messageId)) return prev;
      return [...prev, messageId];
    });
  }, []);

  useEffect(() => {
    if (pendingSeenMessageIds.length === 0) return undefined;

    const idsToProcess = [...pendingSeenMessageIds];
    const timeoutId = setTimeout(() => {
      void (async () => {
        try {
          setCurrentChat((prev) =>
            prev.map((chat) =>
              chat.id && idsToProcess.includes(chat.id)
                ? { ...chat, seen: true }
                : chat,
            ),
          );

          await markMessageAsSeen({ ids: idsToProcess });

          setPendingSeenMessageIds((prev) =>
            prev.filter((id) => !idsToProcess.includes(id)),
          );
        } catch (error) {
          handleError(error);
        }
      })();
    }, debounceMs);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [debounceMs, pendingSeenMessageIds, setCurrentChat]);

  return {
    handleMarkMessageVisible,
  };
};

export default useKemMarkMessagesSeen;
